const PLATFORMS = {
    android: { name: "Android", full: "Android" },
    ios: { name: "iOS", full: "iOS" },
    windows: { name: "Windows", full: "Windows" },
    all: { name: "All Platforms", full: "All Platforms" },
};

const mdmSections = [
    {
        id: "enrollment",
        title: "Enrollment & Onboarding",
        note: "Start here - get every device type into management",
        cards: [
            {
                t: "Zero-Touch Enrollment",
                icon: "device_hub",
                platform: "android",
                d: "Enroll corporate Android devices automatically, straight out of the box, with no manual setup step.",
                learn: [
                    "Configure zero-touch enrollment for a batch of new Android devices",
                    "Skip manual setup so devices come out of the box already managed",
                    "Confirm an enrolled device lands in the console automatically",
                ],
                videoId: "WI98rym3AeU",
                videoType: "youtube",
            },
            {
                t: "Enroll BYOD iOS Devices",
                icon: "phone_iphone",
                platform: "ios",
                d: "Bring personal iOS devices into management without taking over the whole device.",
                learn: [
                    "Walk through the BYOD enrollment flow for iOS",
                    "Separate what's managed from what stays personal",
                    "Handle a user removing enrollment on their own device",
                ],
                videoId: "pgpahvRSD88",
                videoType: "youtube",
            },
            {
                t: "How to Onboard a Windows Device?",
                icon: "checklist",
                platform: "windows",
                d: "Get a Windows endpoint enrolled and reporting into the console.",
                learn: [
                    "Enroll a Windows device into MDM",
                    "Verify the device appears with the right status in the console",
                    "Spot common onboarding issues before they block rollout",
                ],
                videoId: "pMibXFJPmuo",
                videoType: "youtube",
            },
            {
                t: "Getting Started Configuration",
                icon: "settings",
                platform: "windows",
                d: "The baseline setup every Windows rollout starts from before any policy goes out.",
                learn: [
                    "Walk through the initial configuration checklist for Windows MDM",
                    "Confirm the console is ready to enroll and manage Windows devices",
                    "Avoid the common setup steps teams skip and pay for later",
                ],
                videoId: "jJy_qMmo8WA",
                videoType: "youtube",
            },
            {
                t: "Enrollment via Work Account",
                icon: "badge",
                platform: "windows",
                d: "Enroll a Windows device by signing in with a work or school account.",
                learn: [
                    "Enroll a Windows device using a work account sign-in",
                    "Confirm the device reports in with the right status",
                    "Recognize and fix the most common enrollment failures",
                ],
                videoId: "KnYxaE4lKeI",
                videoType: "youtube",
            },
            {
                t: "Enrollment via PPKG Method",
                icon: "deployed_code",
                platform: "windows",
                d: "Enroll a Windows device using a provisioning package (PPKG) for bulk or offline setup.",
                learn: [
                    "Build a provisioning package for Windows enrollment",
                    "Apply the PPKG to enroll a device without a sign-in flow",
                    "Confirm the device lands in the console after enrollment",
                ],
                videoId: "Po3Mk78m09o",
                videoType: "youtube",
            },
        ],
    },
    {
        id: "data-separation",
        title: "Work & Personal Data Separation",
        note: "Keep corporate data isolated without owning the whole device",
        cards: [
            {
                t: "Android Work & Personal Data Separation",
                icon: "phone_android",
                platform: "android",
                d: "Split a device into a managed work profile and an untouched personal profile.",
                learn: [
                    "Explain what a work profile isolates versus the personal profile",
                    "Apply policy to only the work side of the device",
                    "Reassure users that personal apps and data stay outside IT's reach",
                ],
                videoId: "aesBCy7LyHo",
                videoType: "youtube",
            },
            {
                t: "Kiosk Mode",
                icon: "storefront",
                platform: "android",
                d: "Lock a device down to a single app or a curated set of apps for shared or single-purpose use.",
                learn: [
                    "Configure kiosk mode for a single-purpose device",
                    "Choose between single-app and multi-app kiosk modes",
                    "Prevent users from exiting to the home screen or settings",
                ],
                videoId: "f-gM6hl5I7w",
                videoType: "youtube",
            },
        ],
    },
    {
        id: "device-management",
        title: "Device & Group Management",
        note: "Organize endpoints so policy reaches the right machines",
        cards: [
            {
                t: "Windows Devices and Device Groups Management",
                icon: "groups",
                platform: "windows",
                d: "Organize Windows endpoints into groups so policy applies to the right machines.",
                learn: [
                    "Create and structure device groups for Windows endpoints",
                    "Move devices between groups without breaking active policy",
                    "Use groups as the building block for every later policy",
                ],
                videoId: "aIg2Ue-EcBg",
                videoType: "youtube",
            },
        ],
    },
    {
        id: "security-hardening",
        title: "Device Security & Hardening",
        note: "Lock down lost, stolen, or out-of-policy devices",
        cards: [
            {
                t: "Prevent Hard Factory Reset",
                icon: "reset_settings",
                platform: "android",
                d: "Stop a factory reset from removing management, so a lost or stolen device can't be wiped clean of policy.",
                learn: [
                    "Enable factory reset protection on a managed device",
                    "Explain why this matters for lost or stolen devices",
                    "Confirm the device stays enrolled after a reset attempt",
                ],
                videoId: "LE6iOrZ7v3Q",
                videoType: "youtube",
            },
            {
                t: "Windows MDM BitLocker Policy Creation",
                icon: "lock",
                platform: "windows",
                d: "Build a BitLocker policy and target it to the right device groups.",
                learn: [
                    "Create a BitLocker encryption policy from scratch",
                    "Choose the right device groups to target",
                    "Understand the settings that affect recovery key handling",
                ],
                videoId: "Oep54z1mAsI",
                videoType: "youtube",
            },
            {
                t: "Schedule Security Patch and Software Updates",
                icon: "deployed_code_history",
                platform: "all",
                d: "Push OS security patches and software updates to the fleet on a schedule instead of chasing devices manually.",
                learn: [
                    "Schedule patch and update rollout windows for managed devices",
                    "Keep the fleet current without disrupting users mid-task",
                    "Track which devices are on the latest patch level",
                ],
                videoId: "2rbDs5kRbek",
                videoType: "youtube",
            },
        ],
    },
    {
        id: "location-remote",
        title: "Location & Remote Management",
        note: "Track, monitor and remotely control devices in the field",
        cards: [
            {
                t: "Location Tracking and Geofencing",
                icon: "location_on",
                platform: "all",
                d: "See where managed devices are and trigger policy automatically when they cross a geofence boundary.",
                learn: [
                    "View real-time and historical device location",
                    "Draw a geofence and attach an action to entry or exit",
                    "Use location data to enforce a site-specific policy",
                ],
                videoId: "nhiPMMhoytU",
                videoType: "youtube",
            },
            {
                t: "Remote Cast Control",
                icon: "install_desktop",
                platform: "all",
                d: "View and control a managed device's screen remotely for support and troubleshooting.",
                learn: [
                    "Start a remote screen session on a managed device",
                    "Use remote control to troubleshoot without a site visit",
                    "Know what the end user sees while a session is active",
                ],
                videoId: "wrUnert3r6s",
                videoType: "youtube",
            },
        ],
    },
];

const getCardsByPlatform = (platform) => (
    mdmSections.flatMap(s => s.cards).filter(c => c.platform === platform)
);

const androidCards = getCardsByPlatform("android");

const iosCards = [
    {
        t: "Enroll BYOD iOS Devices",
        icon: "phone_iphone",
        platform: "ios",
        d: "Bring personal iOS devices into management without taking over the whole device.",
        learn: [
            "Walk through the BYOD enrollment flow for iOS",
            "Separate what's managed from what stays personal",
            "Handle a user removing enrollment on their own device",
        ],
        videoId: "pgpahvRSD88",
        videoType: "youtube",
    },
];

const windowsSections = [
    {
        id: "getting-started",
        title: "Windows MDM Getting Started",
        note: "The baseline setup every Windows rollout starts from",
        cards: [
            {
                t: "Getting Started Configuration",
                icon: "settings",
                platform: "windows",
                d: "The baseline setup every Windows rollout starts from before any policy goes out.",
                learn: [
                    "Walk through the initial configuration checklist for Windows MDM",
                    "Confirm the console is ready to enroll and manage Windows devices",
                    "Avoid the common setup steps teams skip and pay for later",
                ],
                videoId: "jJy_qMmo8WA",
                videoType: "youtube",
            },
        ],
    },
    {
        id: "enrollment",
        title: "Enrollment & Onboarding",
        note: "Start here - get every device type into management",
        cards: [
            {
                t: "Enrollment via Work Account",
                icon: "badge",
                platform: "windows",
                d: "Enroll a Windows device by signing in with a work or school account.",
                learn: [
                    "Enroll a Windows device using a work account sign-in",
                    "Confirm the device reports in with the right status",
                    "Recognize and fix the most common enrollment failures",
                ],
                videoId: "KnYxaE4lKeI",
                videoType: "youtube",
            },
            {
                t: "Enrollment via PPKG Method",
                icon: "deployed_code",
                platform: "windows",
                d: "Enroll a Windows device using a provisioning package (PPKG) for bulk or offline setup.",
                learn: [
                    "Build a provisioning package for Windows enrollment",
                    "Apply the PPKG to enroll a device without a sign-in flow",
                    "Confirm the device lands in the console after enrollment",
                ],
                videoId: "Po3Mk78m09o",
                videoType: "youtube",
            },
        ],
    },
    {
        id: "device-management",
        title: "Device & Group Management",
        note: "Organize endpoints so policy reaches the right machines",
        cards: [
            {
                t: "Windows Devices and Device Groups Management",
                icon: "groups",
                platform: "windows",
                d: "Organize Windows endpoints into groups so policy applies to the right machines.",
                learn: [
                    "Create and structure device groups for Windows endpoints",
                    "Move devices between groups without breaking active policy",
                    "Use groups as the building block for every later policy",
                ],
                videoId: "aIg2Ue-EcBg",
                videoType: "youtube",
            },
        ],
    },
    {
        id: "policy-security",
        title: "Windows Policy and Security",
        note: "Build and enforce policy across the fleet",
        cards: [
            {
                t: "Windows MDM BitLocker Policy Creation",
                icon: "lock",
                platform: "windows",
                d: "Build a BitLocker policy and target it to the right device groups.",
                learn: [
                    "Create a BitLocker encryption policy from scratch",
                    "Choose the right device groups to target",
                    "Understand the settings that affect recovery key handling",
                ],
                videoId: "Oep54z1mAsI",
                videoType: "youtube",
            },
        ],
    },
];

export { mdmSections, PLATFORMS, androidCards, iosCards, windowsSections };
